import type { RatingStanding, RatingUnqualifiedDisplay } from '@brush/api-client';

/**
 * §9.5's standings table as a pure function — what the screen shows, and what it has to say about
 * what it is not showing.
 *
 * **Everything here is a decision that fails silently.** A renumbered rank, an unstated hidden
 * count and two exclusions collapsed into one figure all produce a table that looks perfectly
 * reasonable, and in each case the only reader who could tell is the player the number is about.
 * That is why this is a module with tests rather than three `filter` calls inside a component —
 * `board.test.js` names the mutation each of them survives.
 *
 * It computes nothing about a rating. Every figure comes from the server, which is §1.1's
 * projection rule reaching the client: a screen that re-derived a rank or a total would be a second
 * implementation free to disagree with the one the room is judged on.
 */

/** What the reader has asked for. Neither of these may ever change a rank. */
export interface StandingsFilter {
  /** §9.5's toggle. False is the default — qualified players only, with the count stated. */
  showAll: boolean;

  /** §9.5's search, over name **and** card id. */
  search: string;
}

export interface StandingsView {
  /**
   * The rows to render, in the server's order, **carrying the server's ranks**.
   *
   * Never re-sorted: §3.7 puts every provisional player below every qualified one whatever their
   * rating, so a client sorting by rating would undo the one rule the minimum exists to enforce.
   */
  rows: readonly RatingStanding[];

  /** Everything the computation produced, which is what makes a partial table readable as one. */
  total: number;
  qualified: number;
  provisional: number;

  /**
   * Rows the qualification filter is holding back. §9.5 requires this **stated**: a table showing
   * 283 of 1,095 with no count is a table that looks like the whole board.
   */
  hiddenByQualification: number;

  /** Rows the search removed. Counted apart, so "no matches" and "none qualified" read apart. */
  hiddenBySearch: number;
}

/**
 * §9.5's table, filtered.
 *
 * **The ranks are the server's and are never touched.** That is the rule §9.5 states outright, and
 * it is the one a reader removes as an implementation detail — a table that renumbered would look
 * right, and a tie makes it wrong even with no filter applied, since ranks legitimately run
 * 1, 2, 2, 4. The rows are returned as they arrived and the component renders `row.rank`.
 */
export function standingsView(
  rows: readonly RatingStanding[],
  filter: StandingsFilter,
  display: RatingUnqualifiedDisplay,
): StandingsView {
  const qualified = rows.filter((r) => r.isQualified).length;

  // §4.6's three rules, and they are three arrangements rather than one with a flag.
  //
  //   hidden      — the BOARD's instruction, not the reader's preference. The rows stay out
  //                 whatever the toggle says, and there is no toggle (`offersProvisionalToggle`).
  //   ranked      — the board asked for interleaving, so hiding them would leave *interior* rank
  //                 gaps rather than the truncated tail §9.5's default relies on. Everybody shows.
  //   provisional — §9.5's own default: qualified only, with the count and a way to see the rest.
  const showProvisional =
    display === 'ranked' ? true : display === 'hidden' ? false : filter.showAll;

  const byQualification = showProvisional ? rows : rows.filter((r) => r.isQualified);
  const query = filter.search.trim().toLowerCase();

  const visible = query
    ? byQualification.filter((r) => matches(r, query))
    : byQualification;

  return {
    rows: visible,
    total: rows.length,
    qualified,
    provisional: rows.length - qualified,
    hiddenByQualification: rows.length - byQualification.length,
    hiddenBySearch: byQualification.length - visible.length,
  };
}

/**
 * §9.5's search: **name and card id**, separately rather than against a joined string.
 *
 * The card is the half that would be quietly dropped, and it is the half an operator uses — they
 * are reading a payout sheet, which carries a number and not always a name. It matches the way
 * `PlayerPicker` already does, for the same reason: somebody has one string and does not know
 * which column it belongs to.
 */
function matches(row: RatingStanding, query: string): boolean {
  return (
    row.playerName.toLowerCase().includes(query) ||
    (row.cardId?.toLowerCase().includes(query) ?? false)
  );
}

/**
 * Whether §9.5's qualified/all toggle is a control this board has.
 *
 * Only under `provisional`. Under `hidden` the board has said its provisional players are not to
 * be shown, so a toggle would be a control that does nothing; under `ranked` they are already in
 * the list, so it would be a control with nothing to reveal. Both are the furniture a switcher
 * with one useful position always is.
 */
export function offersProvisionalToggle(display: RatingUnqualifiedDisplay): boolean {
  return display === 'provisional';
}

/**
 * §4.3's exclusions, said on the screen — which §9.5 requires rather than merely permits.
 *
 * **The two counts are different facts and the sentence keeps them apart.** `excludedAsUnknown` is
 * a player the room holds no city or state for, dropped by the cohort's default policy: that is the
 * silent disappearance, the one where somebody asks why they are not on the board and nobody can
 * say. `excludedFromCohort` is a player the room knows is somebody else's, which is the cohort
 * doing its job. A single combined figure would bury the first inside the second.
 *
 * Null when neither fired — the ordinary case, since a cohort constraining nothing excludes nobody
 * (§4.3's "an empty list is no constraint"), and a notice that appeared on every board would stop
 * being read by the time it mattered.
 */
export function exclusionNotice(standings: {
  excludedAsUnknown: number;
  excludedFromCohort: number;
}): string | null {
  const parts: string[] = [];

  if (standings.excludedAsUnknown > 0) {
    parts.push(
      `${people(standings.excludedAsUnknown)} left out because this room has no city or state ` +
        `on their player card`,
    );
  }

  if (standings.excludedFromCohort > 0) {
    parts.push(`${people(standings.excludedFromCohort)} outside this cohort`);
  }

  return parts.length === 0 ? null : `${parts.join(', and ')}.`;
}

/** "1 players" is the kind of sentence that makes a reader distrust the rest of the screen. */
function people(count: number): string {
  return count === 1 ? '1 player' : `${count} players`;
}

/**
 * Whether this board can have published standings at all — **and therefore whether to ask.**
 *
 * <b>A draft cannot, ever.</b> Not "has not yet": `POST /recalculate` and `POST /close` both refuse
 * a draft (`rating_board_not_started`), and the scheduled sweep only touches runnable boards, so
 * nothing in the product can put a snapshot behind one. The answer to `GET /standings` for a draft
 * is known before the request is made.
 *
 * <b>What asking anyway cost.</b> The screen fired both reads for every board. On a draft that
 * meant a request whose answer was a foregone conclusion, and — because a null body cannot be said
 * over HTTP (see `RatingBoardStandingsDto`) — an error card underneath a correct-looking empty
 * state, telling an operator to check their connection about a request that had answered 200. The
 * second read is `POST /compute`, which runs the engine over the room's whole history: the most
 * expensive operation in the feature, on a screen that discards its answer, since everything that
 * reads it sits inside the "there is a snapshot" branch.
 *
 * <b>An unrecognised status is asked.</b> Not asking is the destructive default here — it renders an
 * empty board with no error anywhere — so a status this does not know about gets the request and
 * lets the server answer. A new status must not be able to blank the screen silently.
 *
 * It takes the status rather than the board so that the rule is checkable without one, and so the
 * one thing it depends on is visible in the signature.
 */
export function publishesStandings(status: string): boolean {
  return status !== 'draft';
}

/** §9.5's empty standings state — see {@link emptyStandingsNotice}. */
export interface EmptyStandings {
  /** The fact, in one line. */
  lead: string;

  /** What to do about it, and what this state is not. */
  detail: string;
}

/**
 * What a board with nothing published says — **two arrangements, because the next action differs.**
 *
 * <b>A draft is sent to Start and a started board to Recalculate.</b> One sentence for both told
 * every operator to recalculate, which a draft refuses (`rating_board_not_started`) — a screen
 * sending somebody to a control that says no. The recalculation panel directly above already says
 * Start for a draft, so the two halves of one screen disagreed about the next action on the same
 * board, which is how an operator concludes the product is broken rather than unfinished.
 *
 * <b>Both keep "nothing published" apart from "nobody scored".</b> That distinction is what the
 * whole section turns on and it survives in both arrangements deliberately: one sends an operator to
 * a button, the other to the results, and a screen that blurred them would send everybody to the
 * button.
 *
 * An unrecognised status takes the ordinary arrangement, matching {@link publishesStandings}: the
 * draft arrangement is the special case, so a status this does not know about must not inherit it
 * and tell somebody to start a board that is already running.
 */
export function emptyStandingsNotice(status: string): EmptyStandings {
  const closing = 'Until then there is nothing frozen to show — which is not the same as nobody having scored.';

  if (status === 'draft') {
    return {
      lead: 'This rating is still a draft, so it has published nothing.',
      detail: `Start it above to pin its points system, and it can then be worked out. ${closing}`,
    };
  }

  return {
    lead: 'This rating has not been worked out yet.',
    detail: `Recalculate above to publish its first standings. ${closing}`,
  };
}

/**
 * §9.2's preview pane, as a view — **the figures and the rule that hides some of them come from the
 * SAME computation, and there is no parameter by which they could come from anywhere else.**
 *
 * <b>That signature is the fix rather than a tidy-up.</b> The pane took a display rule as an
 * argument and the builder passed it the **draft's**, while the rows came from `POST /compute`,
 * which scores the board **as stored**. So inside §4.6's own section one field was live and its
 * neighbour was not: changing the minimum did nothing (the server had already decided `isQualified`
 * from the saved one), and changing the display rule hid rows immediately. An operator who did both
 * read the second as the first taking effect. The caption under the pane says *as saved* about both,
 * so it was true of one control and false of the one beside it, with nothing saying which.
 *
 * <b>The rule is the server's, off the response.</b> Not parsed out of the board's `qualification`
 * document — that is opaque JSON on the wire, and reading it here would be a second reader of a
 * document the engine has already read. `RatingBoardStandings` takes it off the response for exactly
 * this reason.
 *
 * The filter is fixed: no search, no toggle. This is twelve rows beside an editor, not the standings
 * screen — `offersProvisionalToggle` is about a screen that has one.
 */
export function previewView(
  live: {
    data: readonly RatingStanding[];
    unqualifiedDisplay?: string | null;
  } | null,
): StandingsView | null {
  if (live === null) return null;

  const display = (live.unqualifiedDisplay ?? 'provisional') as RatingUnqualifiedDisplay;

  return standingsView(live.data, { showAll: false, search: '' }, display);
}

/**
 * §9.2's preview pane with no rows in it — **two states, and one sentence covered both with the
 * wrong advice for each.**
 *
 * It read *"Nobody is rated by this yet. Widen the events or the window above, or lower the
 * minimum."* Two things wrong with that, in opposite directions:
 *
 * <b>The minimum cannot empty this pane.</b> §3.6 is normative that qualification governs
 * presentation and ranking and never computation — `HiddenCount`'s own contract says unqualified
 * players are in `data` regardless — so an empty `total` means the engine produced nothing: no
 * events in scope, nobody in the cohort, nothing scored. An operator who follows that sentence drops
 * the minimum to 0, watches nothing change, and concludes the pane is broken.
 *
 * <b>And where it IS the advice, "nobody is rated by this yet" is false.</b> A board with 348 rated
 * players, every one below the minimum, shows an empty list under a sentence saying it has nobody.
 * That state said nothing at all.
 *
 * So the states are named apart, which is the same call {@link emptyStandingsNotice} makes about the
 * standings screen's two: the sentence is only worth writing if it names the control that reaches
 * the state it appears in.
 *
 * A pane with rows, or one with nothing computed yet, has no notice — the second is the spinner's
 * business and an empty sentence under a loading pane is a fault report about a request in flight.
 */
export function previewEmptyNotice(view: StandingsView | null): EmptyStandings | null {
  if (view === null || view.rows.length > 0) return null;

  // Rows exist and every one is held back: the minimum is exactly what reaches this, and the count
  // is what stops the sentence claiming the board is empty when it is full.
  if (view.total > 0) {
    return {
      lead: `All ${view.total} rated ${view.total === 1 ? 'player is' : 'players are'} below the minimum.`,
      detail:
        'Lower the minimum in section 7, or set it to show provisional players, and they will ' +
        'appear here.',
    };
  }

  return {
    lead: 'Nobody is rated by this yet.',
    detail:
      'Widen the events or the window above, or the players this rates. The minimum does not ' +
      'reach this — it decides who counts as rated, not who is scored.',
  };
}
